import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * The dashboard's metric tiles in the order this user chose. `order` is their saved list of
 * tile keys - tiles not in it (new ones, or never moved) keep their place from the default
 * order, after the ones they come after. Dragging only starts after 8px, so a click still
 * opens the tile.
 */
export const orderTiles = (tiles, order) => {
    if (!order?.length) return tiles;
    const rank = new Map(order.map((key, i) => [key, i]));
    const known = tiles.filter((t) => rank.has(t.key)).sort((a, b) => rank.get(a.key) - rank.get(b.key));
    // a tile the user hasn't placed goes back in after the tile it follows by default
    const result = [...known];
    tiles.forEach((tile, i) => {
        if (rank.has(tile.key)) return;
        const before = tiles.slice(0, i).reverse().find((t) => rank.has(t.key) || result.includes(t));
        const at = before ? result.indexOf(before) + 1 : 0;
        result.splice(at, 0, tile);
    });
    return result;
};

const SortableTile = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <div
            ref={setNodeRef}
            className={`sortable-tile${isDragging ? ' sortable-tile-dragging' : ''}`}
            style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 5 : undefined }}
            data-tile={id}
            {...attributes}
            {...listeners}
        >
            {children}
        </div>
    );
};

export const SortableTiles = ({ tiles, order, onReorder }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const ordered = orderTiles(tiles, order);
    const keys = ordered.map((t) => t.key);

    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        // what's shown now, plus anything placed before but hidden at the moment (another
        // module's tile) so hiding a tile doesn't lose its place
        const moved = arrayMove(keys, keys.indexOf(active.id), keys.indexOf(over.id));
        const hidden = (order || []).filter((key) => !keys.includes(key));
        onReorder([...moved, ...hidden]);
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={keys} strategy={rectSortingStrategy}>
                <div className="metrics-grid">
                    {ordered.map((tile) => <SortableTile key={tile.key} id={tile.key}>{tile.node}</SortableTile>)}
                </div>
            </SortableContext>
        </DndContext>
    );
};
